import classNames from "classnames";
import { type ActiveView } from "../../state";
import { useI18n } from "../../i18n";
import styles from "./ViewSwitcher.module.css";
import { type JSX, type KeyboardEvent, useRef } from "react";

type ViewSwitcherProps = {
  activeView: ActiveView;
  onSwitch: (view: ActiveView) => void;
};

const tabs: ActiveView[] = ["leasing", "budget"];

const ViewSwitcher = ({ activeView, onSwitch }: ViewSwitcherProps): JSX.Element => {
  const { t } = useI18n();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    let newIndex: number | null = null;

    if (event.key === "ArrowRight") {
      newIndex = (index + 1) % tabs.length;
    } else if (event.key === "ArrowLeft") {
      newIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      newIndex = 0;
    } else if (event.key === "End") {
      newIndex = tabs.length - 1;
    }

    if (newIndex !== null) {
      event.preventDefault();
      tabRefs.current[newIndex]?.focus();
      onSwitch(tabs[newIndex]);
    }
  };

  const labels: Record<ActiveView, string> = {
    leasing: t("viewSwitcher.leasingCalculator"),
    budget: t("viewSwitcher.budgetCalculator"),
  };

  return (
    <div className={styles.tabBar} role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab}
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          role="tab"
          id={`tab-${tab}`}
          aria-selected={activeView === tab}
          aria-controls={`tabpanel-${tab}`}
          tabIndex={activeView === tab ? 0 : -1}
          className={classNames(styles.tab, { [styles.active]: activeView === tab })}
          onClick={() => onSwitch(tab)}
          onKeyDown={(e) => handleKeyDown(e, index)}
        >
          {labels[tab]}
        </button>
      ))}
    </div>
  );
};

export { ViewSwitcher };
